import { useCallback, useRef, useState } from 'react';
import type { Id } from '../../api/types';
import { useProjects } from '../../projects/ProjectsProvider';
import { Popover } from '../ui/Popover';
import { EmptyState } from '../ui/EmptyState';
import { ErrorNotice, StaleBanner } from '../ui/Notice';
import { Spinner } from '../ui/Skeleton';
import { ProjectOption } from './ProjectOption';

/**
 * 🔴 THE MOBILE PROJECT SWITCHER — the top-bar half of the phone answer.
 *
 * A labelled control sitting in the top bar, showing the project you are on, opening a list. ⛔ NOT
 * a hamburger drawer: a drawer hides both the current project and the fact that switching is
 * possible behind a glyph you have to already know about. The whole point of putting it in the top
 * bar is that "TBS ▾" answers "which project am I looking at?" without any interaction at all.
 *
 * ⭐ The panel itself is `Popover`, which below 640px is a BOTTOM SHEET — the list opens next to the
 * thumb rather than as a 260px menu pinned under the top bar. The trigger is discoverable at the
 * top; the choosing happens at the bottom. That split is deliberate.
 *
 * ⭐ Views are NOT in here. Project and view are two different questions and Or asked specifically
 * how you change VIEW on a phone — the answer is the always-visible bottom tab bar, not a second
 * level inside this menu.
 *
 * 🔴 The trigger tells the truth about the fetch. When `/projects` failed there is no project name
 * to show, so it says so on its face rather than silently reading "All projects", which is a
 * different and reassuring claim.
 */
export function ProjectSwitcher({
  projectId,
  onSelect,
}: {
  projectId: Id | null;
  onSelect: (projectId: Id | null) => void;
}) {
  const projects = useProjects();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => setOpen(false), []);
  const choose = useCallback(
    (next: Id | null) => {
      onSelect(next);
      close();
    },
    [onSelect, close],
  );

  const items = projects.data;
  const selected = items?.find((project) => project.id === projectId) ?? null;
  const unresolved = projectId !== null && items !== null && selected === null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-8 min-w-0 max-w-[7.5rem] items-center gap-1.5 rounded border border-[color:var(--color-line)] px-2 text-sm font-semibold md:hidden"
      >
        <span className="sr-only">Project: </span>
        {projects.isLoading ? (
          <>
            <Spinner label="Loading projects" />
            <span className="truncate font-medium text-[color:var(--color-ink-muted)]">
              Projects…
            </span>
          </>
        ) : projects.error && !items ? (
          <span className="truncate font-medium text-amber-700 dark:text-amber-300">
            <span aria-hidden="true">⚠️</span> Projects unavailable
          </span>
        ) : (
          <>
            {projects.isStale ? (
              <span aria-label="Project list is out of date" title="Project list is out of date">
                ⚠️
              </span>
            ) : null}
            <span className="truncate">
              {selected ? selected.key : unresolved ? `#${projectId}` : 'All projects'}
            </span>
          </>
        )}
        <span aria-hidden="true" className="ml-auto text-[color:var(--color-ink-muted)]">
          ▾
        </span>
      </button>

      <Popover anchorRef={buttonRef} open={open} onClose={close} label="Switch project">
        <div className="space-y-2 p-2">
          {projects.error && !projects.isStale ? (
            <ErrorNotice error={projects.error} onRetry={projects.reload} />
          ) : null}

          {projects.isStale && projects.error ? (
            <StaleBanner
              error={projects.error}
              loadedAt={projects.loadedAt}
              onRetry={projects.reload}
            />
          ) : null}

          {/* ⚠️ The URL names a project the loaded list does not contain. Saying so beats silently
              showing "All projects" — the list below is NOT filtered the way the link claims. */}
          {unresolved ? (
            <p className="rounded border border-amber-300 bg-amber-50 px-2.5 py-2 text-sm text-amber-950 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-100">
              The link names project <code className="font-mono">#{projectId}</code>, which is not in
              this list — it may have been removed, or you may not have access.
            </p>
          ) : null}

          {items && items.length === 0 && !projects.error ? (
            <EmptyState title="No projects yet" detail="The request succeeded and returned none." />
          ) : null}

          {items && items.length > 0 ? (
            <ul>
              <li>
                <ProjectOption
                  project={null}
                  selected={projectId === null}
                  touch
                  onSelect={() => choose(null)}
                />
              </li>
              {items.map((project) => (
                <li key={project.id}>
                  <ProjectOption
                    project={project}
                    selected={project.id === projectId}
                    touch
                    onSelect={() => choose(project.id)}
                  />
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Popover>
    </>
  );
}
